
export interface ScreenSizes {
    minScreenWidth: number;
    mediaQuery: string;
    sizesSupported: googletag.SingleSize[];
    labels: string[];
}

export declare type SizeMapping = ScreenSizes[];

export const billboard: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [
            [980, 250], [980, 120], [980, 90],
            [970, 250], [970, 90],
            [960, 90],
            [950, 90],
            [930, 180],
            [800, 250], [800, 90],
            [768, 90],
            [750, 200], [750, 100],
            [728, 90],
        ],
        labels: [ 'billboard-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'billboard-mobile' ],
    },
];

export const rectangleDesktopOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[336, 280], [300, 250], [250, 250], 'fluid'],
        labels: [ 'rectangle-desktop-only-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'rectangle-desktop-only-mobile' ],
    },
];

export const rectangleContentDesktopOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[600, 300], 'fluid'],
        labels: [ 'rectangle-desktop-only-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'rectangle-desktop-only-mobile' ],
    },
];

export const rectangleMobileOnly: SizeMapping = [
    {
        minScreenWidth: 600, // desktop (not shown)
        mediaQuery: '(min-width: 600px)',
        sizesSupported: [],
        labels: [ 'rectangle-mobile-only-desktop' ],
    },
    {
        minScreenWidth: 360,  // mobile (nexus5 - 360px)
        mediaQuery: '(min-width: 360px) and (max-width: 599px)',
        sizesSupported: [[336, 280], [300, 250], [250, 250], 'fluid'],
        labels: [ 'rectangle-mobile-only-nexus5' ],
    },
    {
        minScreenWidth: 0,  // mobile (iphone4 - 320px)
        mediaQuery: '(max-width: 359px)',
        sizesSupported: [[300, 250], [250, 250], 'fluid'],
        labels: [ 'rectangle-mobile-only-iphone4' ],
    },
];

export const halfpage: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[300, 600], [160, 600], [120, 600], 'fluid'],
        labels: [ 'halfpage-desktop' ],
    },
    {
        minScreenWidth: 0,  // mobile (not shown)
        mediaQuery: '(max-width: 979px)', // mobile (not shown)
        sizesSupported: [],
        labels: [ 'halfpage-mobile' ],
    },
];

export const halfpageMobileOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop (not shown)
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [],
        labels: [ 'halfpage-mobile-only-desktop' ],
    },
    {
        minScreenWidth: 0,  // mobile
        mediaQuery: '(max-width: 979px)', // mobile
        sizesSupported: [[300, 600], [160, 600], [120, 600], 'fluid'],
        labels: [ 'halfpage-mobile-only-mobile' ],
    },
];

export const halfpageOffsite: SizeMapping = [
    {
        minScreenWidth: 1590, // desktop (1590 - 980 stranka - 10 margin) / 2 >= 300
        mediaQuery: '(min-width: 1590px)',
        sizesSupported: [[300, 600], [160, 600], [120, 600]],
        labels: [ 'halfpage-offsite-1590' ],
    },
    {
        minScreenWidth: 1310, // desktop  (1310 - 980 stranka - 10 margin) / 2 >= 160
        mediaQuery: '(min-width: 1310px) and (max-width: 1589px)',
        sizesSupported: [[160, 600], [120, 600]],
        labels: [ 'halfpage-offsite-1310' ],
    },
    {
        minScreenWidth: 1230, // desktop  (1230 - 980 stranka - 10 margin) / 2 >= 120
        mediaQuery: '(min-width: 1230px) and (max-width: 1319px)',
        sizesSupported: [[120, 600]],
        labels: [ 'halfpage-offsite-1230' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 1229px)',
        sizesSupported: [],
        labels: [ 'halfpage-offsite-mobile' ],
    },
];

export const brandingDesktopOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[2000, 1400], [970, 250]],
        labels: [ 'branding-desktop-only-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'branding-desktop-only-mobile' ],
    },
];

export const brandingMobileOnly: SizeMapping = [
    {
        minScreenWidth: 600, // desktop (not shown)
        mediaQuery: '(min-width: 600px)',
        sizesSupported: [],
        labels: [ 'branding-mobile-only-desktop' ],
    },
    {
        // Creative is wider than screen width. When displaying, we center it
        // inside slot and hide parts that overflow
        minScreenWidth: 0,  // mobile (iphone 4/5 - 320px)
        mediaQuery: '(max-width: 599px)',
        sizesSupported: [[320, 125], [320, 100]],
        labels: [ 'branding-mobile-only-mobile' ],
    },
    {
        minScreenWidth: 360,  // mobile (samsung galaxy 6/7/8/9 - 360px
        mediaQuery: '(max-width: 599px)',
        sizesSupported: [[360, 125], [358, 112], [320, 100]],
        labels: [ 'branding-mobile-only-mobile' ],
    },
    {
        minScreenWidth: 375,  // mobile (iphone 6/7/8/X/XS - 375px
        mediaQuery: '(max-width: 599px)',
        sizesSupported: [[375, 125], [374, 117], [358, 112], [320, 100]],
        labels: [ 'branding-mobile-only-mobile-375' ],
    },
    {
        minScreenWidth: 410,  // mobile (iphone 6/7/8 Plus/XS Max/XR - 414px, pixel 1/2/3 XL - 412px, samsung galaxy 8/9 plus 412px
        mediaQuery: '(max-width: 599px)',
        sizesSupported: [[420, 125], [420, 122], [400, 125], [390, 122], [374, 117], [358, 112], [320, 100]],
        labels: [ 'branding-mobile-only-mobile-410' ],
    },
];

export const mail: SizeMapping = [
    {
        minScreenWidth: 0,
        mediaQuery: '(min-width: 0px)',
        sizesSupported: [[730, 3000], 'fluid'],
        labels: [ 'mail' ],
    },
];

export const mailCtaButton = [
    {
        minScreenWidth: 0,
        mediaQuery: '(min-width: 0px)',
        sizesSupported: ['fluid'],
        labels: [ 'mailCtaButton' ],
    },
];

export const homepagePost = [
    {
        minScreenWidth: 0,
        mediaQuery: '(min-width: 0px)',
        // [336, 82] is a dummy size, to achieve placeholder with correct height while loading ad, improving CLS of hp
        sizesSupported: [[336, 82], 'fluid'],
        labels: [ 'homepage-post' ],
    },
];

export const nativeRectangleDesktopOnly: SizeMapping = [
    {
        minScreenWidth: 980, // desktop
        mediaQuery: '(min-width: 980px)',
        sizesSupported: [[336, 280], 'fluid'],
        labels: [ 'native-rectangle-desktop-only-desktop' ],
    },
    {
        minScreenWidth: 0, // mobile (not shown)
        mediaQuery: '(max-width: 979px)',
        sizesSupported: [],
        labels: [ 'native-rectangle-desktop-only-mobile' ],
    },
];

export const nativeRectangleMobileOnly: SizeMapping = [
    {
        minScreenWidth: 600, // desktop (not shown)
        mediaQuery: '(min-width: 600px)',
        sizesSupported: [],
        labels: [ 'native-rectangle-mobile-only-desktop' ],
    },
    {
        minScreenWidth: 360,  // mobile (nexus5 - 360px)
        mediaQuery: '(min-width: 360px) and (max-width: 599px)',
        sizesSupported: [[336, 280], 'fluid'],
        labels: [ 'native-rectangle-mobile-only-nexus5' ],
    },
    {
        minScreenWidth: 0,  // mobile (iphone4 - 320px)
        mediaQuery: '(max-width: 359px)',
        sizesSupported: ['fluid'],
        labels: [ 'native-rectangle-mobile-only-iphone4' ],
    },
];


// https://support.google.com/admanager/answer/6015986?hl=en
export const interstitialMobileOnly: SizeMapping = [
    {
        minScreenWidth: 600, // desktop (not shown)
        mediaQuery: '(min-width: 600px)',
        sizesSupported: [[1, 1]],
        labels: [ 'interstitial-mobile-only-desktop' ],
    },
    {
        // Creative is wider than screen width. When displaying, we center it
        // inside slot and hide parts that overflow
        minScreenWidth: 0,  // mobile (iphone4 - 320px; up to iphone 6+ - 416px)
        mediaQuery: '(max-width: 599px)',
        sizesSupported: [[1, 1]],
        labels: [ 'interstitial-mobile-only-mobile' ],
    },
];

// list of all sizes - used for setting sizes in prebid config for header bidding
export default [
    ...billboard,
    ...rectangleDesktopOnly,
    ...rectangleMobileOnly,
    ...rectangleContentDesktopOnly,
    ...halfpage,
    ...halfpageMobileOnly,
    ...halfpageOffsite,
    ...brandingDesktopOnly,
    ...brandingMobileOnly,
    // ...mail,                         // not used for hb
    // ...mailCtaButton,                // not used for hb
    ...homepagePost,
    // ...interstitialMobileOnly,       // not used for hb
    // ...nativeRectangleDesktopOnly,   // not used for hb
    // ...nativeRectangleMobileOnly,    // not used for hb
];
